import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  FileText,
  ShoppingCart,
  Warehouse,
  Boxes,
  Users,
  Package,
  Wallet,
  Truck,
  Building2,
  ClipboardList,
  Receipt,
  TrendingUp,
  Landmark,
  Settings as SettingsIcon,
  ScrollText,
  FileBarChart,
  LogOut,
  ChevronDown,
  Download,
  Menu,
  X,
  IndianRupee,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import NotificationsBell from "@/components/NotificationsBell";

const NAV = [
  { to: "/", label: "Purchase Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/accounts-dashboard", label: "Accounts Dashboard", icon: IndianRupee, testid: "nav-accounts-dashboard" },
  { to: "/mr", label: "MR", icon: TrendingUp, testid: "nav-mr" },
  { to: "/account", label: "Account", icon: Landmark, testid: "nav-account" },
  { to: "/budget-sheet", label: "Budget Sheet", icon: ClipboardList, testid: "nav-budget-sheet" },
  { to: "/indents", label: "Indent Sheet", icon: FileText, testid: "nav-indents" },
  { to: "/purchase", label: "PO Log", icon: ShoppingCart, testid: "nav-purchase" },
  { to: "/store", label: "Store", icon: Warehouse, testid: "nav-store" },
  { to: "/indent-log", label: "Store Log", icon: Boxes, testid: "nav-indent-log" },
  { to: "/po-register", label: "Sales Management", icon: Receipt, testid: "nav-sales-management" },
  { to: "/customer-po-register", label: "Customer PO Register", icon: ClipboardList, testid: "nav-customer-po-register" },
  { to: "/reports", label: "Reports", icon: FileBarChart, testid: "nav-reports" },
  { to: "/audit-logs", label: "Audit Logs", icon: ScrollText, testid: "nav-audit-logs" },
];

const MASTERS = [
  { to: "/masters/intender", label: "Intender Master", icon: Users, testid: "nav-intender" },
  { to: "/masters/item", label: "Item Master", icon: Package, testid: "nav-item" },
  { to: "/masters/category", label: "Category Master", icon: ClipboardList, testid: "nav-category" },
  { to: "/masters/supplier", label: "Supplier Master", icon: Truck, testid: "nav-supplier" },
  { to: "/masters/cost-center", label: "Cost Center Master", icon: Wallet, testid: "nav-cost-center" },
  { to: "/masters/required", label: "Required Master", icon: ClipboardList, testid: "nav-required" },
  { to: "/masters/department", label: "Department Master", icon: Building2, testid: "nav-department" },
  { to: "/masters/process", label: "Process Master", icon: ClipboardList, testid: "nav-process" },
  { to: "/masters/company", label: "Company Master", icon: Building2, testid: "nav-company" },
  { to: "/masters/customer", label: "Customer Master", icon: Users, testid: "nav-customer" },
  { to: "/masters/bank", label: "Bank Master", icon: Landmark, testid: "nav-bank" },
];

function SidebarLink({ to, label, icon: Icon, end, testid, onNavigate }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testid}
      onClick={onNavigate}
      className={({ isActive }) =>
        `pms-sidebar-item flex items-center gap-3 px-4 py-2.5 text-sm font-medium ${
          isActive ? "active text-white" : "text-slate-300"
        }`
      }
    >
      <Icon size={18} strokeWidth={1.75} />
      <span>{label}</span>
    </NavLink>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [exporting, setExporting] = React.useState(false);
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const closeSidebar = () => setSidebarOpen(false);
  const initials = (user?.name || "U")
    .split(" ")
    .map((s) => s[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const canExport = user?.role === "admin" || user?.role === "gm";

  const handleFullExport = async () => {
    if (exporting) return;
    setExporting(true);
    const t = toast.loading("Generating full Excel export…");
    try {
      const res = await api.get("/export/full-excel", { responseType: "blob" });
      const today = new Date().toISOString().slice(0, 10);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `CPMS_Full_Export_${today}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export downloaded", { id: t });
    } catch (err) {
      // Server returned an error as a Blob — read it as text to surface the real reason
      let msg = "Export failed";
      try {
        const blob = err?.response?.data;
        if (blob && typeof blob.text === "function") {
          const txt = await blob.text();
          try { msg = JSON.parse(txt).detail || msg; }
          catch { if (txt) msg = txt; }
        } else if (err?.message) {
          msg = err.message;
        }
      } catch { /* keep default msg */ }
      const status = err?.response?.status;
      if (status === 401) msg = "Session expired — please log in again";
      if (status === 502 || status === 503 || status === 504) msg = "Server warming up — try again in a few seconds";
      toast.error(msg, { id: t });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Mobile overlay (hidden on lg+) */}
      {sidebarOpen && (
        <div
          onClick={closeSidebar}
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          data-testid="sidebar-overlay"
          aria-hidden="true"
        />
      )}

      {/* Sidebar — drawer on mobile, permanent on lg+ */}
      <aside
        className={`w-64 bg-[#1D2951] text-slate-300 flex flex-col fixed inset-y-0 left-0 z-40 transform transition-transform duration-200 ease-out ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0`}
        data-testid="sidebar"
      >
        {/* Close button — mobile only */}
        <button
          type="button"
          onClick={closeSidebar}
          className="lg:hidden absolute top-3 right-3 p-1.5 text-slate-300 hover:text-white hover:bg-white/10 rounded-md"
          data-testid="sidebar-close-btn"
          aria-label="Close menu"
        >
          <X size={18} />
        </button>

        <div className="h-24 flex items-center px-5 border-b border-white/10 gap-3">
          <img
            src="/logo.png"
            alt="CPMS Logo"
            className="w-20 h-20 object-contain drop-shadow-sm shrink-0"
            data-testid="sidebar-logo"
          />
          <div>
            <div className="text-white font-bold text-xl tracking-tight leading-none">CPMS</div>
            <div className="text-[10px] text-slate-400 uppercase tracking-widest mt-1.5">
              Purchase Mgmt
            </div>
          </div>
        </div>

        <nav className="flex-1 py-4 space-y-0.5 overflow-y-auto">
          {NAV.map((n) => (
            <SidebarLink key={n.to} {...n} onNavigate={closeSidebar} />
          ))}

          <div className="px-4 pt-6 pb-1">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">
              Masters
            </div>
          </div>
          {MASTERS.map((n) => (
            <SidebarLink key={n.to} {...n} onNavigate={closeSidebar} />
          ))}

          <div className="px-4 pt-6 pb-1">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">
              System
            </div>
          </div>
          {user?.role === "admin" && (
            <SidebarLink to="/inward-log" label="Inward Log" icon={ScrollText} testid="nav-inward-log" onNavigate={closeSidebar} />
          )}
          <SidebarLink to="/settings" label="Settings" icon={SettingsIcon} testid="nav-settings" onNavigate={closeSidebar} />
        </nav>

        <div className="p-4 border-t border-white/10 text-[11px] text-slate-400">
          <div>v1.0 · Industrial Build</div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 lg:ml-64 min-w-0 w-full">
        <header
          className="h-16 bg-white border-b border-slate-200 sticky top-0 z-20 flex items-center justify-between px-4 sm:px-6"
          data-testid="topbar"
        >
          <div className="flex items-center gap-3 min-w-0">
            {/* Hamburger — mobile only */}
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-1.5 -ml-1 text-slate-700 hover:bg-slate-100 rounded-md"
              data-testid="sidebar-open-btn"
              aria-label="Open menu"
            >
              <Menu size={20} />
            </button>
            <div className="text-sm text-slate-500 truncate">
              <span className="font-mono uppercase tracking-wider text-[11px] hidden sm:inline">
                Workspace ·
              </span>{" "}
              <span className="text-slate-800 font-medium">{user?.role === "admin" ? "Administrator" : "Operations"}</span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {canExport && (
              <button
                onClick={handleFullExport}
                disabled={exporting}
                data-testid="full-export-btn"
                title="Download a single .xlsx with every master and log on a separate sheet"
                className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider px-3 py-1.5 rounded-md border border-[#1D2951]/20 text-[#1D2951] hover:bg-[#1D2951] hover:text-white transition-colors disabled:opacity-60"
              >
                <Download size={14} />
                <span className="hidden sm:inline">{exporting ? "Exporting…" : "Export All"}</span>
              </button>
            )}
            <NotificationsBell />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-slate-100"
                  data-testid="user-menu-trigger"
                >
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-[#1D2951] text-white text-xs">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="text-left hidden sm:block">
                    <div className="text-xs font-semibold text-slate-900 leading-tight">
                      {user?.name}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">
                      {user?.role}
                    </div>
                  </div>
                  <ChevronDown size={14} className="text-slate-500" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="font-semibold">{user?.name}</div>
                  <div className="text-xs text-slate-500">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/settings")}>
                  <SettingsIcon size={14} className="mr-2" /> Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="logout-btn">
                  <LogOut size={14} className="mr-2" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="p-4 sm:p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
